// ---------------------------------------------------------------------------
// Workspace Settings.
//
// Reported defect: this screen presented invented facts as measured ones, and
// contradicted /studio/billing while doing it. Signed out, it said
// "Current: Creator Plan ($9/mo)" and "Shared outputs: 3/10 · Source storage:
// 0.5GB/5GB" while Billing — reading the real `subscriptions` table — said
// "No active subscription". Six controls (Edit, Export Settings, Review
// Records, Connect Domain, Delete Account, and three notification checkboxes)
// looked pressable and did nothing at all, including the Danger Zone's
// account deletion.
//
// The rule applied throughout: state what was measured, say "unknown" when
// nothing was measured, and never render a control that only looks live. Where
// a real action exists it is wired; where none exists the control is `disabled`
// and carries a *visible* reason, following the "Library next actions" pattern
// in library-hub-page.tsx.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react'
import { PageHeader } from '@/components/page-header'
import { useStudioStore } from '@/store/studio-store'
import { Button } from '@/components/ui/button'
import { GitHubConnectSection } from '@/components/github-connect-section'
import { pushStudioRoute } from '@/lib/studio-navigation'
import { cogoXyzRegistry } from '@/features/cogo-xyz/cogo-xyz-registry'
import { loadCogoXyzRegistryFromCurrentProject } from '@/features/cogo-xyz/cogo-xyz-project-storage'
import { DashboardContainer } from '../components/dashboard-container'
import {
  planDetail,
  planHeadline,
  useStudioBilling,
} from '../lib/studio-billing-source'
import {
  PROFILE_SIGNED_OUT_REASON,
  displayNameEditBlockedReason,
  updateStudioDisplayName,
} from '../lib/studio-profile-source'
import { useStudioSettingsWriteOutcome } from '@/hooks/use-studio-settings-write-outcome'
// The IDE's own persona control, mounted here as well rather than
// reimplemented — one definition of what a persona is and how it is written.
import { PersonaSwitcher } from '@/shell/ide-v2/components/persona-switcher'
import { COOGO_IDE_CHROME_COPY } from '@/shell/ide-v2/coogo-ide-chrome-copy'
import {
  isClosedNetworkDeployment,
  readStudioPresentationIdentity,
  reduceStudioPresentationProfile,
  type StudioPresentationProfile,
} from '@/services/studio-presentation-profile'
import { probeStudioPlatform } from '@/services/studio-capability-probes'
import { RuntimeCapabilityPanel } from '../components/runtime-capability-panel'
import { buildPlanRightsViewModel } from '../lib/plan-rights-view-model'
import { getProject, type StudioProject } from '@/services/project-api'
import { useStudioLocale } from '@/i18n/use-studio-locale'

const UNKNOWN = '—'

/**
 * A profile write outcome as the creator sees it. The tone is carried from the
 * result of the write rather than inferred from the message text, so a refusal
 * can never be dressed as an acceptance.
 */
type ProfileNotice = { tone: 'error' | 'success'; message: string }

// Matches the inline notice pair in hero-chat-input.tsx: a refusal is
// destructive and announced assertively, an acceptance is quiet and polite.
const PROFILE_NOTICE_STYLE: Record<ProfileNotice['tone'], string> = {
  error:
    'rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-xs text-destructive',
  success:
    'rounded-md border border-border/70 bg-background px-2 py-1.5 text-xs text-muted-foreground',
}

/**
 * A control with no implementation behind it: rendered `disabled`, with the
 * reason both in the accessible name and on screen. Never a dead click.
 */
function BlockedAction({
  label,
  reason,
  testId,
  variant = 'outline',
}: {
  label: string
  reason: string
  testId: string
  variant?: 'outline' | 'destructive'
}) {
  return (
    <div className="space-y-1.5">
      <Button
        variant={variant}
        size="lg"
        className="px-3 text-sm"
        disabled
        data-testid={testId}
        aria-label={`${label} — ${reason}`}
        title={reason}
      >
        {label}
      </Button>
      <p
        className="text-xs leading-5 text-muted-foreground"
        data-testid={`${testId}-reason`}
      >
        {reason}
      </p>
    </div>
  )
}

export function SettingsPage() {
  const { strings, locale } = useStudioLocale()
  const settingsCopy = strings.settings
  const personaCopy = COOGO_IDE_CHROME_COPY[locale].persona
  const user = useStudioStore((s) => s.user)
  const isAuthLoading = useStudioStore((s) => s.isAuthLoading)
  const setStudioRoute = useStudioStore((s) => s.setStudioRoute)
  const billing = useStudioBilling()
  const writeOutcome = useStudioSettingsWriteOutcome()
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const [planRightsReadback, setPlanRightsReadback] = useState<{
    projectId: string
    project: StudioProject | null
  } | null>(null)
  const [runtimeProfile, setRuntimeProfile] = useState<StudioPresentationProfile>(
    () =>
      reduceStudioPresentationProfile(readStudioPresentationIdentity(), {
        probes: {},
        operations: {},
      }),
  )

  useEffect(() => {
    let mounted = true
    void probeStudioPlatform().then((platformConnection) => {
      if (!mounted) return
      setRuntimeProfile(
        reduceStudioPresentationProfile(readStudioPresentationIdentity(), {
          probes: { 'platform-connection': platformConnection },
          operations: {},
        }),
      )
    })
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    let active = true
    if (!currentProjectId) {
      setPlanRightsReadback(null)
      return () => {
        active = false
      }
    }

    void getProject(currentProjectId)
      .then((project) => {
        if (active) setPlanRightsReadback({ projectId: currentProjectId, project })
      })
      .catch(() => {
        if (active) {
          setPlanRightsReadback({ projectId: currentProjectId, project: null })
        }
      })
    return () => {
      active = false
    }
  }, [currentProjectId])

  function goTo(route: 'billing' | 'library') {
    setStudioRoute(route)
    pushStudioRoute(route)
  }

  // On a closed network the outside services behind several of these controls
  // (a hosted subscription, public DNS, github.com, a vendor mailbox) do not
  // answer — the edge CSP refuses the request before it leaves. The same rule
  // this screen already applies to unimplemented controls applies to them:
  // disabled, with the actual reason visible, BEFORE they are pressed. Cloud
  // builds are untouched — this flag is only set by the appliance artifact.
  const closedNetwork = isClosedNetworkDeployment()

  const signedIn = Boolean(user)
  // No fabricated fallback: signed out, we do not know this person's name, so
  // the field reads "—" rather than inventing "Creator".
  const displayName =
    (user?.user_metadata?.full_name as string | undefined) ??
    user?.email?.split('@')[0] ??
    null

  // ── Profile editing ──────────────────────────────────────────────────────
  // The lib decides WHETHER the control is blocked and WHY; the screen renders
  // that reason in the reader's language. The two causes stay distinct —
  // collapsing them told a signed-out visitor their provider was at fault.
  const profileBlockedSource = displayNameEditBlockedReason(signedIn)
  const profileBlockedReason =
    profileBlockedSource === null
      ? null
      : profileBlockedSource === PROFILE_SIGNED_OUT_REASON
        ? settingsCopy.profile.signedOutBlocked
        : settingsCopy.profile.providerBlocked
  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [savingName, setSavingName] = useState(false)
  const [profileNotice, setProfileNotice] = useState<ProfileNotice | null>(null)

  async function saveDisplayName() {
    setSavingName(true)
    setProfileNotice(null)
    const result = await updateStudioDisplayName(nameDraft)
    setSavingName(false)
    // "Saved" is only ever shown after the write is accepted, and the tone
    // comes from that same answer — never from reading the message back.
    if (result.ok) {
      setEditingName(false)
      setProfileNotice({ tone: 'success', message: settingsCopy.profile.nameSaved })
    } else {
      setProfileNotice({ tone: 'error', message: result.error })
    }
  }

  // ── Publishing records ───────────────────────────────────────────────────
  // The old "Artifact exports: [Ready] (packet-6411)" was a hardcoded badge
  // next to a hardcoded internal identifier that referred to nothing. These
  // counts come from the same registry the Library screen reads.
  const [exportCount, setExportCount] = useState(
    () =>
      cogoXyzRegistry
        .snapshot()
        .toolRuns.filter((run) => run.toolName === 'export_cogo_bundle').length,
  )
  const [recordCount, setRecordCount] = useState(
    () => cogoXyzRegistry.snapshot().evidence.length,
  )

  useEffect(() => {
    let mounted = true
    void loadCogoXyzRegistryFromCurrentProject().finally(() => {
      if (!mounted) return
      const snapshot = cogoXyzRegistry.snapshot()
      setExportCount(
        snapshot.toolRuns.filter((run) => run.toolName === 'export_cogo_bundle')
          .length,
      )
      setRecordCount(snapshot.evidence.length)
    })
    return () => {
      mounted = false
    }
  }, [])

  const planLine = planHeadline(billing, settingsCopy.plan)
  const planSupportingLine = planDetail(billing)
  const planRights = buildPlanRightsViewModel(
    planRightsReadback?.projectId === currentProjectId
      ? planRightsReadback.project
      : null,
  )

  return (
    <DashboardContainer
      className="py-6 space-y-6"
      data-testid="studio-settings-page"
    >
      <PageHeader route="settings" title={settingsCopy.title} />

      {writeOutcome.state === 'protected-write-rejected' ? (
        <div
          role="alert"
          data-testid="settings-protected-write-rejected"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-4"
        >
          <p className="text-sm font-semibold text-foreground">
            {settingsCopy.writeOutcome.protectedRejected}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {writeOutcome.message}
          </p>
        </div>
      ) : writeOutcome.state === 'accepted' ? (
        <p className="text-sm text-muted-foreground">
          {settingsCopy.writeOutcome.accepted}
        </p>
      ) : null}

      <section className="rounded-xl border border-border bg-card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-foreground">{settingsCopy.profile.title}</h2>
        <div className="space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-muted-foreground">{settingsCopy.profile.name}</p>
              {editingName ? (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <input
                    value={nameDraft}
                    autoFocus
                    onChange={(event) => setNameDraft(event.target.value)}
                    aria-label={settingsCopy.profile.nameInputLabel}
                    data-testid="settings-name-input"
                    className="h-10 rounded-lg border border-border bg-background px-3 text-sm text-foreground outline-none focus:border-foreground/40"
                  />
                  <Button
                    size="lg"
                    className="px-3 text-sm"
                    // Only the in-flight write disables Save. A blank name is
                    // rejected by `updateStudioDisplayName`, which returns the
                    // reason shown below — disabling the button here instead
                    // made that rejection unreachable, so clearing the name and
                    // pressing Save did nothing and explained nothing.
                    disabled={savingName}
                    data-testid="settings-name-save"
                    onClick={() => void saveDisplayName()}
                  >
                    {savingName
                      ? settingsCopy.profile.saving
                      : settingsCopy.profile.save}
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="px-3 text-sm"
                    disabled={savingName}
                    data-testid="settings-name-cancel"
                    onClick={() => {
                      setEditingName(false)
                      setProfileNotice(null)
                    }}
                  >
                    {settingsCopy.profile.cancel}
                  </Button>
                </div>
              ) : (
                <p className="text-sm font-medium" data-testid="settings-name">
                  {displayName ?? UNKNOWN}
                </p>
              )}
            </div>
            {!editingName &&
              (profileBlockedReason ? (
                <BlockedAction
                  label={settingsCopy.profile.edit}
                  reason={profileBlockedReason}
                  testId="settings-profile-edit"
                />
              ) : (
                <Button
                  variant="outline"
                  size="lg"
                  className="px-3 text-sm"
                  data-testid="settings-profile-edit"
                  onClick={() => {
                    setNameDraft(displayName ?? '')
                    setProfileNotice(null)
                    setEditingName(true)
                  }}
                >
                  {settingsCopy.profile.edit}
                </Button>
              ))}
          </div>
          {profileNotice && (
            <p
              role={profileNotice.tone === 'error' ? 'alert' : 'status'}
              data-tone={profileNotice.tone}
              className={PROFILE_NOTICE_STYLE[profileNotice.tone]}
              data-testid="settings-profile-notice"
            >
              {profileNotice.message}
            </p>
          )}
          <div>
            <p className="text-sm text-muted-foreground">{settingsCopy.profile.email}</p>
            <p className="text-sm font-medium">{user?.email ?? UNKNOWN}</p>
          </div>
        </div>
      </section>

      {/*
        Wave 7 item 9 (slice 6 §3): `studioPersona` defaults to `creator` and
        its only control lived in the IDE top bar, so a developer could not
        choose the developer persona until they had already opened a project
        (`B3-persona-on-dashboard.visible: false`). The persona survives
        reload and project exit — it was always a preference; only its
        reachability was wrong. It sits with the other preferences now, and
        the IDE's control is mounted rather than duplicated.
      */}
      <section className="rounded-xl border border-border bg-card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-foreground">
          {personaCopy.toolVisibility}
        </h2>
        <p className="text-sm text-muted-foreground">
          {personaCopy.settingsDetail}
        </p>
        <PersonaSwitcher />
      </section>

      <section className="rounded-xl border border-border bg-card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-foreground">{settingsCopy.publishing.title}</h2>
        <div className="space-y-1">
          <p className="text-sm" data-testid="settings-export-status">
            {settingsCopy.publishing.exportsLabel}{' '}
            <span className="font-medium">
              {exportCount === 0
                ? settingsCopy.publishing.exportsNone
                : settingsCopy.publishing.exportsCount.replace(
                    '{count}',
                    String(exportCount),
                  )}
            </span>
          </p>
          <p className="text-xs text-muted-foreground">
            {recordCount === 0
              ? settingsCopy.publishing.recordsNone
              : settingsCopy.publishing.recordsCount.replace(
                  '{count}',
                  String(recordCount),
                )}
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-3">
          <BlockedAction
            label={settingsCopy.publishing.exportSettings}
            reason={settingsCopy.publishing.exportSettingsBlocked}
            testId="settings-export-settings"
          />
          {recordCount === 0 ? (
            <BlockedAction
              label={settingsCopy.publishing.reviewRecords}
              reason={settingsCopy.publishing.reviewRecordsBlocked}
              testId="settings-review-records"
            />
          ) : (
            <Button
              variant="outline"
              size="lg"
              className="px-3 text-sm"
              data-testid="settings-review-records"
              onClick={() => goTo('library')}
            >
              {settingsCopy.publishing.reviewRecords}
            </Button>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-foreground">{settingsCopy.plan.title}</h2>
        {closedNetwork ? (
          <>
            {/* An appliance's entitlement is its installed licence. Rendering
                the subscription lookup here would show a permanent failure for
                a table that CANNOT answer on this network — a broken screen
                for a healthy machine. */}
            <p
              className="text-sm text-muted-foreground"
              data-testid="settings-plan-headline"
            >
              {settingsCopy.closedNetwork.planNote}
            </p>
            <BlockedAction
              label={settingsCopy.plan.manage}
              reason={settingsCopy.closedNetwork.planManageBlocked}
              testId="settings-manage-plan"
            />
          </>
        ) : (
          <>
            <div className="space-y-2">
              <p className="text-sm" data-testid="settings-plan-headline">
                {settingsCopy.plan.current}{' '}
                <span className="font-medium">{planLine}</span>
              </p>
              {planSupportingLine && (
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="settings-plan-detail"
                >
                  {planSupportingLine}
                </p>
              )}
              {billing.state === 'error' && billing.error && (
                <p className="text-xs text-muted-foreground">{billing.error}</p>
              )}
            </div>
            <Button
              size="lg"
              className="px-3 text-sm"
              data-testid="settings-manage-plan"
              onClick={() => goTo('billing')}
            >
              {settingsCopy.plan.manage}
            </Button>
          </>
        )}

        <div className="border-t border-border pt-4">
          {/* FINDING-005: "Generation rights readback" is pipeline vocabulary;
              the section answers a user question, so it is titled as one. The
              value strings below stay verbatim — they are pinned by
              settings-page-truthfulness + studio-detailed-data-audit (e2e)
              and rewording them belongs to a lane that re-runs that evidence. */}
          <h3 className="text-xs font-medium text-muted-foreground">
            {settingsCopy.plan.allowsTitle}
          </h3>
          <dl className="mt-2 grid gap-3 sm:grid-cols-3" data-testid="settings-plan-rights">
            <div>
              <dt className="text-xs text-muted-foreground">{settingsCopy.plan.generation}</dt>
              <dd className="text-sm font-medium">
                {settingsCopy.plan.rights[planRights.generation]}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{settingsCopy.plan.publication}</dt>
              <dd className="text-sm font-medium">
                {settingsCopy.plan.rights[planRights.publication]}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{settingsCopy.plan.packPromotion}</dt>
              <dd className="text-sm font-medium">
                {settingsCopy.plan.rights[planRights.packPromotion]}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <RuntimeCapabilityPanel profile={runtimeProfile} />

      <section className="rounded-xl border border-border bg-card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-foreground">{settingsCopy.domain.title}</h2>
        <div className="space-y-2">
          {closedNetwork ? (
            // The one address that is true here is the one the reader is
            // already using. Deriving "<name>.cogo.xyz" on an appliance names
            // a host that does not exist on this network.
            <p className="text-sm" data-testid="settings-domain-served-from">
              <span className="text-muted-foreground">
                {settingsCopy.closedNetwork.servedFromLabel}
              </span>{' '}
              {window.location.origin}
            </p>
          ) : (
            <>
              {/* The default domain was previously derived from a fabricated
                  "Creator" name, so a signed-out visitor was shown
                  "creator.cogo.xyz" as if it were theirs. */}
              <p className="text-sm">
                <span className="text-muted-foreground">{settingsCopy.domain.defaultLabel}</span>{' '}
                {displayName
                  ? `${displayName.toLowerCase().replace(/\s/g, '')}.cogo.xyz`
                  : isAuthLoading
                    ? UNKNOWN
                    : settingsCopy.domain.signedOut}
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">{settingsCopy.domain.customLabel}</span> {UNKNOWN}
              </p>
            </>
          )}
        </div>
        <BlockedAction
          label={settingsCopy.domain.connect}
          reason={
            closedNetwork
              ? settingsCopy.closedNetwork.domainBlocked
              : settingsCopy.domain.blocked
          }
          testId="settings-connect-domain"
        />
      </section>

      {closedNetwork ? (
        // GitHubConnectSection opens an OAuth popup to github.com; on this
        // network that popup can never come back. Same section slot, honest
        // control.
        <section className="rounded-xl border border-border bg-card p-6 space-y-4">
          <h2 className="text-sm font-semibold text-foreground">
            {settingsCopy.closedNetwork.githubTitle}
          </h2>
          <BlockedAction
            label={settingsCopy.github.connect}
            reason={settingsCopy.closedNetwork.githubBlocked}
            testId="settings-github-connect"
          />
        </section>
      ) : (
        <GitHubConnectSection />
      )}

      <section className="rounded-xl border border-border bg-card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-foreground">{settingsCopy.notifications.title}</h2>
        {/* These three checkboxes were `defaultChecked` with no `onChange` and
            no store behind them: they toggled, claimed two of three emails were
            already switched on, and nothing was ever recorded. Studio has no
            service that sends these emails and no table that holds the
            preference, so they are inert and say why. */}
        <p
          className="text-xs leading-5 text-muted-foreground"
          data-testid="settings-notifications-reason"
        >
          {settingsCopy.notifications.reason}
        </p>
        <div className="space-y-2">
          {[
            settingsCopy.notifications.review,
            settingsCopy.notifications.agentSummary,
            settingsCopy.notifications.productUpdates,
          ].map((label) => (
            <label
              key={label}
              className="flex min-h-10 cursor-not-allowed items-center gap-2 opacity-60"
            >
              <input
                type="checkbox"
                checked={false}
                disabled
                readOnly
                aria-label={`${label} ${settingsCopy.notifications.unavailableSuffix}`}
                className="rounded border-border"
              />
              <span className="text-sm text-foreground">{label}</span>
            </label>
          ))}
        </div>
      </section>

      {/* Danger Zone is the canonical destructive surface, so it reads the
          --destructive token rather than a raw red-* palette step. The nested
          BlockedAction already renders variant="destructive"; a hardcoded
          red-200/red-50/red-600 frame around it drifted from that token on
          every theme change. */}
      <section className="rounded-xl border border-destructive/25 bg-destructive/5 p-6 space-y-4">
        <h2 className="text-sm font-semibold text-destructive">{settingsCopy.danger.title}</h2>
        {/* A destructive control that silently does nothing is the worst case
            of this defect class: the user believes the account is gone. */}
        <BlockedAction
          label={settingsCopy.danger.deleteAccount}
          // The cloud reason points at privacy@cogo.xyz — a mailbox on the
          // public internet. On a closed network the accountable party is
          // whoever administers the machine, and the copy says so.
          reason={
            closedNetwork
              ? settingsCopy.closedNetwork.deleteBlocked
              : settingsCopy.danger.deleteBlocked
          }
          testId="settings-delete-account"
          variant="destructive"
        />
      </section>
    </DashboardContainer>
  )
}
